import React, { useEffect, useState } from 'react';
import { AddShareItemsInput, ShareItem } from 'types/Share';
import useShareObject from 'views/Shares/hooks/useShareObject';
import {
    NButton,
    NNotification,
    NStatusTag,
    NTable,
} from '@innersource-nn/novoui-react';
import { useNavigate } from 'react-router-dom';
import Pagination from 'components/pager/Pagination';
import { ShareableObjectFilter } from 'types/Filters';
import { sorting } from 'utils/tableSorting';
import { TableSortStateType, TableSortState } from '@novonordisk/novoui-vuejs';
import useEditShareRequestItems from 'views/Shares/hooks/useEditShareRequestItems';
import { UserRoleForDataset } from 'types/Dataset';
import { getTagColor } from 'utils/getTagColorForShare';
import Spinner from 'components/loader/Spinner';

const tableHeaders = [
    {
        header: 'Name',
        key: 'itemName',
        isSortable: true,
        initialSortState: TableSortState.asc,
    },
    {
        header: 'Type',
        key: 'itemType',
        isSortable: true,
    },
    {
        header: 'Status',
        key: 'status',
    },
];

const confirmationHeader = [
    {
        header: 'Name',
        key: 'itemName',
    },
    {
        header: 'Type',
        key: 'itemType',
    },
];
interface ShareItemsTableProps {
    shareUri: string;
    resourceUri: string;
    onClose: VoidFunction | undefined;
    handleNext: (data?: {
        autoApproved?: boolean;
        creationError?: string;
    }) => void | { autoApproved?: boolean; creationError?: string };
    buttonLabel: string;
}

// selects items in a dataset and request access to them press next
const ShareItemsTable = ({
    shareUri,
    resourceUri,
    onClose,
    handleNext,
    buttonLabel,
}: ShareItemsTableProps) => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<ShareableObjectFilter>({
        page: 1,
        pageSize: 500,
    });

    const { notification, loading, shareableItems, role } = useShareObject(
        shareUri,
        filter,
        resourceUri,
    );

    const editShareRequestItems = useEditShareRequestItems(
        role === UserRoleForDataset.BusinessOwner ||
            role === UserRoleForDataset.DataSteward,
    );
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [disabledRows, setDisabledRows] = useState<string[]>([]);
    const [addShareItems, setAddShareItems] = useState<ShareItem[]>([]);
    const [removeShareItems, setRemoveShareItems] = useState<ShareItem[]>([]);
    const [shareItemsToBeRemoved, setShareItemsToBeRemoved] = useState<
        ShareItem[]
    >([]);
    const [sortedItems, setSortedItems] = useState<ShareItem[]>(
        shareableItems.nodes,
    );

    const sortItems = (sortMap: Map<string, TableSortStateType>) => {
        setSortedItems(sorting(sortMap, sortedItems, true));
    };

    useEffect(() => {
        setAddShareItems(
            shareableItems.nodes.filter(
                (item: ShareItem) =>
                    selectedRows.includes(item.name) && !item.checked,
            ),
        );

        setRemoveShareItems(
            shareableItems.nodes.filter(
                (item: ShareItem) =>
                    !selectedRows.includes(item.name) && item.status !== null,
            ),
        );
    }, [selectedRows, shareableItems.nodes]);

    useEffect(() => {
        if (shareableItems.nodes.length !== 0) {
            setSortedItems(shareableItems.nodes);
            const checkedItemNames = shareableItems.nodes
                .filter((item) => item.checked)
                .map((item) => item.name);
            setSelectedRows(checkedItemNames);
            const disabledNewItems = checkTypesAndDisable(checkedItemNames);

            const disabledItemNames = shareableItems.nodes
                .filter((item) => item.disabled)
                .map((item) => item.name);
            setDisabledRows([...disabledItemNames, ...disabledNewItems]);
        }
    }, [shareableItems]);

    const checkForSharedItems = async () => {
        const sharedItems = removeShareItems.filter(
            (item) => item.status === 'Shared' || item.status === 'Approved',
        );

        if (sharedItems.length > 0) {
            setShareItemsToBeRemoved(sharedItems);
        } else {
            handleRequestAccess();
        }
    };

    const handleRequestAccess = async () => {
        setLoadingSubmit(true);
        const addShareItemsInput: AddShareItemsInput[] = addShareItems.map(
            (item) => ({
                itemUri: item.itemUri,
                itemType: item.itemType,
            }),
        );

        await editShareRequestItems({
            addShareItemsInput,
            removeShareItems,
            shareUri,
            handleNext,
        });

        setLoadingSubmit(false);
    };

    const handleClick = (uri: string) => {
        navigate(uri);
        if (onClose) onClose();
    };

    const checkTypesAndDisable = (newItems: string[]) => {
        const disabledItems: string[] = [];
        newItems.forEach((newItem) => {
            // If an S3Bucket item is selected StorageLocations cant be selected
            if (newItem.includes('S3Bucket')) {
                const storageLocationItems = shareableItems.nodes
                    .filter((item) => item.itemType === 'StorageLocation')
                    .map((item) => item.name);

                disabledItems.push(...storageLocationItems);
            } else if (newItem.includes('StorageLocation')) {
                // If an StorageLocation item is selected S3Bucket cant be selected
                const s3BucketItems = shareableItems.nodes
                    .filter((item) => item.itemType === 'S3Bucket')
                    .map((item) => item.name);
                disabledItems.push(...s3BucketItems);
            }
        });
        return disabledItems;
    };
    const checkSelectedType = (setter: React.SetStateAction<string[]>) => {
        const resolvedSetter =
            typeof setter === 'function' ? setter(selectedRows) : setter;

        const newItems = resolvedSetter.filter(
            (item) => !selectedRows.includes(item),
        );
        if (newItems.length > 0) {
            const disabledNewItems = checkTypesAndDisable(newItems);
            disabledRows.push(...disabledNewItems);
        }
        const itemsInSelectedRowsNotInSetter = selectedRows.filter(
            (item) => !resolvedSetter.includes(item),
        );
        itemsInSelectedRowsNotInSetter.forEach((newItem) => {
            // Reset the disabled when a S3Bucket or StorageLocation is unselected
            if (
                newItem.includes('S3Bucket') &&
                disabledRows.filter((item) => item.includes('StorageLocation'))
            ) {
                // Extract names from storageLocationItems
                const storageLocationNames = disabledRows.filter((item) =>
                    item.includes('StorageLocation'),
                );

                // Filter out these names from disabledRows
                const newDisabledRows = disabledRows.filter(
                    (row) => !storageLocationNames.includes(row),
                );
                setDisabledRows(newDisabledRows);
            } else if (
                newItem.includes('StorageLocation') &&
                disabledRows.filter((item) => item.includes('S3Bucket'))
            ) {
                // Extract names from storageLocationItems
                const storageLocationNames = disabledRows.filter((item) =>
                    item.includes('S3Bucket'),
                );

                // Filter out these names from disabledRows
                const newDisabledRows = disabledRows.filter(
                    (row) => !storageLocationNames.includes(row),
                );
                setDisabledRows(newDisabledRows);
            }
        });
        setSelectedRows(setter);
    };
    const adjustItems = (items: ShareItem[]) => {
        return items.map((item) => {
            return {
                ...item,
                status: item.status ? (
                    <NStatusTag
                        label={item.status}
                        state={getTagColor(item.status)}
                    />
                ) : (
                    '-'
                ),
            };
        });
    };
    return shareItemsToBeRemoved.length > 0 ? (
        <div className="text-true-blue flex flex-col gap-16">
            <p>
                You are about to remove access to items you have selected. By
                confirming, you will lose access to:
            </p>
            <NTable
                containerHeight="360px"
                headers={confirmationHeader}
                columnBorder={true}
                items={shareItemsToBeRemoved}
                rowKey="name"
            />
            <div className="mt-12 flex gap-12 justify-end">
                <NButton
                    variant="outline"
                    square
                    onClick={() => setShareItemsToBeRemoved([])}
                >
                    Go back to editing
                </NButton>
                <NButton
                    isLoading={loadingSubmit}
                    variant="fill"
                    square
                    onClick={() => handleRequestAccess()}
                >
                    {addShareItems.length === 0 && removeShareItems.length === 0
                        ? 'Skip step'
                        : buttonLabel}
                </NButton>
            </div>
        </div>
    ) : (
        <div>
            {notification || sortedItems.length === 0 ? (
                <div>
                    <NNotification
                        className="w-full"
                        content={notification?.message}
                        type={notification?.type}
                        inline
                    />
                    <div className="mt-16 flex gap-12">
                        <NButton
                            variant="outline"
                            square
                            onClick={() =>
                                handleClick(`/dataset/${resourceUri}`)
                            }
                        >
                            Go to dataset
                        </NButton>
                        {notification?.type !== 'alert' && (
                            <NButton
                                variant="outline"
                                square
                                onClick={() =>
                                    handleClick(`/share/${shareUri}`)
                                }
                            >
                                Go to share request
                            </NButton>
                        )}
                    </div>
                </div>
            ) : loading ? (
                <Spinner className="h-80" />
            ) : (
                <div className="mt-16">
                    <NTable
                        containerHeight="360px"
                        headers={tableHeaders}
                        columnBorder={true}
                        items={adjustItems(sortedItems)}
                        rowKey="name"
                        selectableRow={true}
                        selectedRowIds={selectedRows}
                        setSelectedRowIds={checkSelectedType}
                        sorting={sortItems}
                        disabledRowIds={disabledRows}
                    />
                    {!loading && shareableItems.count > 0 && (
                        <Pagination
                            items={shareableItems}
                            filter={filter}
                            setFilter={setFilter}
                        />
                    )}
                    <div className="mt-12 flex gap-12 justify-end">
                        <NButton variant="outline" square onClick={onClose}>
                            Cancel
                        </NButton>
                        <NButton
                            isLoading={loadingSubmit}
                            variant={
                                selectedRows.length === 0 ? 'fill' : undefined
                            }
                            square
                            onClick={() =>
                                addShareItems.length === 0 &&
                                removeShareItems.length === 0
                                    ? handleNext()
                                    : checkForSharedItems()
                            }
                        >
                            {addShareItems.length === 0 &&
                            removeShareItems.length === 0
                                ? 'Skip step'
                                : buttonLabel}
                        </NButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareItemsTable;
